import {
  AIRule,
  StatusFilterOption,
  ZoriRulesStatusTab,
  ZoriRulesTab,
} from './types';

export const stubRules: AIRule[] = [
  {
    id: 'rule-stub-1',
    account_id: 'fountainlife',
    channel: 'fountainmembersagent:member-message',
    scope: null,
    condition_description:
      'Member says they ran out of medication or missed doses; alert care team.',
    evaluation_mode: 'llm',
    static_condition: null,
    schedule_delay_hours: null,
    action: 'tool#message_care_team',
    action_params: { urgency: 'high', category: 'medication' },
    visibility: 'opaque',
    status: 'active',
    priority: 10,
    category: 'medication_refill',
    cooldown_seconds: 86400,
    created: new Date().toISOString(),
    created_by: 'clinician@example.com',
    edited_by: 'clinician@example.com',
    last_edited: new Date().toISOString(),
  },
  {
    id: 'rule-stub-2',
    account_id: 'fountainlife',
    channel: 'fountaincliniciansagent:clinician-message',
    scope: 'send_message',
    condition_description:
      'Clinician asks for a summary; execute the executive summary template.',
    evaluation_mode: 'llm',
    static_condition: null,
    schedule_delay_hours: null,
    action: 'tool#execute_template',
    action_params: { template_id: 'executive-summary', audience: 'clinician' },
    visibility: 'transparent',
    status: 'active',
    priority: 7,
    category: null,
    cooldown_seconds: null,
    created: new Date().toISOString(),
    created_by: 'system',
    edited_by: 'system',
    last_edited: new Date().toISOString(),
  },
  {
    id: 'rule-stub-3',
    account_id: 'fountainlife',
    channel: 'patient_change_stream:update',
    scope: 'medication_request',
    condition_description:
      'Medication request changes should be evaluated holistically later.',
    evaluation_mode: 'static',
    static_condition:
      "resourceType == 'MedicationRequest' && action == 'UPDATE'",
    schedule_delay_hours: 6,
    action: 'tool#create_async_task',
    action_params: { agent: 'rule-evaluation-agent' },
    visibility: 'opaque',
    status: 'inactive',
    priority: 5,
    category: 'medication_refill',
    cooldown_seconds: 3600,
    created: new Date().toISOString(),
    created_by: 'system',
    edited_by: 'system',
    last_edited: new Date().toISOString(),
  },
];

export const formatMaybeDateTime = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export const formatDateTime = (value: string | number | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

export const getAuditSubjectMemberId = (subjectId: string | null | undefined) =>
  subjectId?.split(':')[0] ?? '';

export const getUseStubRules = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('stubRules') === '1';
  } catch {
    return false;
  }
};

export const normalizeJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export type RuleFormValidationError =
  | 'condition_required'
  | 'priority_invalid'
  | 'static_condition_required'
  | 'action_params_invalid'
  | 'schedule_delay_invalid'
  | 'cooldown_invalid';

export interface RuleFormValidationInput {
  conditionDescription: string;
  priority: string;
  evaluationMode: 'llm' | 'static';
  staticCondition: string;
  actionParamsJson: string;
  scheduleDelayHours: string;
  cooldownSeconds: string;
}

export const validateRuleForm = (
  input: RuleFormValidationInput,
): RuleFormValidationError | null => {
  if (input.evaluationMode === 'llm' && !input.conditionDescription.trim()) {
    return 'condition_required';
  }
  const parsedPriority = Number(input.priority);
  if (
    Number.isNaN(parsedPriority) ||
    parsedPriority < 1 ||
    parsedPriority > 10
  ) {
    return 'priority_invalid';
  }
  if (input.evaluationMode === 'static' && !input.staticCondition.trim()) {
    return 'static_condition_required';
  }
  try {
    JSON.parse(input.actionParamsJson || '{}');
  } catch {
    return 'action_params_invalid';
  }

  const scheduleDelay =
    input.scheduleDelayHours.trim() === ''
      ? null
      : Number(input.scheduleDelayHours);
  if (scheduleDelay !== null && Number.isNaN(scheduleDelay)) {
    return 'schedule_delay_invalid';
  }

  const cooldownSeconds =
    input.cooldownSeconds.trim() === '' ? null : Number(input.cooldownSeconds);
  if (cooldownSeconds !== null && Number.isNaN(cooldownSeconds)) {
    return 'cooldown_invalid';
  }

  return null;
};

export const statusFilterByTab: Record<ZoriRulesStatusTab, StatusFilterOption> =
  {
    all: 'ALL',
    active: 'ACTIVE',
    inactive: 'INACTIVE',
  };

export type ZoriRulesSectionTab = Extract<
  ZoriRulesTab,
  'rules' | 'knowledge-base' | 'tasks' | 'rule-actions' | 'report-matchers'
>;

export const getZoriRulesSectionTab = (
  tab: string | undefined,
): ZoriRulesSectionTab => {
  if (
    tab === 'knowledge-base' ||
    tab === 'tasks' ||
    tab === 'rule-actions' ||
    tab === 'report-matchers'
  ) {
    return tab;
  }
  return 'rules';
};

export const getZoriRulesStatusTab = (
  tab: string | undefined,
): ZoriRulesStatusTab => {
  if (tab === 'active' || tab === 'inactive') {
    return tab;
  }
  return 'all';
};

export async function fetchAuthedJson<T>(
  path: string,
  authToken: string,
  accountId: string,
): Promise<T> {
  return mutateAuthedJson<T>({
    path,
    authToken,
    accountId,
    method: 'GET',
  });
}

export async function mutateAuthedJson<T>({
  path,
  authToken,
  accountId,
  method,
  body,
}: {
  path: string;
  authToken: string;
  accountId: string;
  method: 'GET' | 'PUT' | 'DELETE' | 'POST' | 'PATCH';
  body?: unknown;
}): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'LifeOmic-Account': accountId,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json();
}

export async function fetchAuditRows<T>(
  path: string,
  authToken: string,
  accountId: string,
): Promise<T[]> {
  return fetchAuthedJson<T[]>(path, authToken, accountId);
}
